import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useTheme,
  Divider,
  Grid,
} from '@mui/material';
import {
  PhotoCamera as CameraIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CameraAlt as WebcamIcon,
  Security,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AuthenticatedAvatar from '../../../components/AuthenticatedAvatar';

const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

function UserProfile() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isCameraReady, setIsCameraReady] = useState(false);

  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    avatar_url: user?.avatar_url || '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        avatar_url: user.avatar_url || '',
      });
    }
  }, [user]);

  // Cleanup camera stream
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    await uploadAvatar(file);
  };

  const uploadAvatar = async (file: File) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'profiles');

      const uploadResponse = await axios.post(`${API_URL}/api/v1/upload/`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const avatarUrl = uploadResponse.data.url;

      // Update profile with new avatar
      await axios.put(
        `${API_URL}/api/v1/auth/me/profile`,
        { avatar_url: avatarUrl },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setFormData(prev => ({ ...prev, avatar_url: avatarUrl }));
      updateUser({ avatar_url: avatarUrl });
      setSuccess('Avatar updated successfully!');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload avatar');
    } finally {
      setLoading(false);
    }
  };

  const handleStartCamera = async () => {
    setCameraDialogOpen(true);
    setError(null);

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: 'user' },
        audio: false,
      });

      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play();
          setIsCameraReady(true);
        };
      }
    } catch (err: any) {
      setError('Failed to access camera: ' + err.message);
      setCameraDialogOpen(false);
    }
  };

  const handleCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;

    // Check if video has valid dimensions
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      setError('Camera not ready. Please wait a moment and try again.');
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(async (blob) => {
        if (blob) {
          const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
          await uploadAvatar(file);
          handleCloseCamera();
        }
      }, 'image/jpeg', 0.9);
    }
  };

  const handleCloseCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCameraReady(false);
    setCameraDialogOpen(false);
  };

  const handleDeleteAvatar = async () => {
    if (!confirm('Are you sure you want to remove your avatar?')) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/v1/auth/me/profile`,
        { avatar_url: null },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setFormData(prev => ({ ...prev, avatar_url: '' }));
      updateUser({ avatar_url: undefined });
      setSuccess('Avatar removed successfully!');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove avatar');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/v1/auth/me/profile`,
        {
          first_name: formData.first_name,
          last_name: formData.last_name,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      updateUser(response.data.user);
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            {t('common.profile')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage your profile information and avatar
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Avatar Section */}
          <Grid item xs={12} md={4}>
            <Card sx={{ border: `1px solid ${theme.palette.divider}`, boxShadow: theme.shadows[1] }}>
              <CardContent>
                <Stack spacing={2} alignItems="center">
                  <Typography variant="h6" fontWeight={600}>
                    {t('common.profilePhoto')}
                  </Typography>

                  <Box sx={{ position: 'relative' }}>
                    <AuthenticatedAvatar
                      src={formData.avatar_url}
                      sx={{
                        width: 150,
                        height: 150,
                        fontSize: '3rem',
                        bgcolor: 'primary.main',
                        fontWeight: 600,
                      }}
                    >
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </AuthenticatedAvatar>
                    {formData.avatar_url && (
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          bgcolor: 'error.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'error.dark' },
                        }}
                        onClick={handleDeleteAvatar}
                        disabled={loading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  <Stack spacing={1} width="100%">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                    />

                    <Button
                      variant="contained"
                      startIcon={<UploadIcon />}
                      fullWidth
                      onClick={() => fileInputRef.current?.click()}
                      disabled={loading}
                    >
                      {t('common.uploadPhoto')}
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<WebcamIcon />}
                      fullWidth
                      onClick={handleStartCamera}
                      disabled={loading}
                    >
                      {t('common.useCamera')}
                    </Button>
                  </Stack>

                  <Typography variant="caption" color="text.secondary" textAlign="center">
                    Recommended: Square image, max 5MB
                    <br />
                    Formats: JPG, PNG, GIF
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Profile Information */}
          <Grid item xs={12} md={8}>
            <Card sx={{ border: `1px solid ${theme.palette.divider}`, boxShadow: theme.shadows[1] }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" fontWeight={600}>
                    {t('common.profileInformation')}
                  </Typography>
                  {!isEditing ? (
                    <Stack direction="row" spacing={1}>
                      <Button startIcon={<Security />} onClick={() => navigate('/consent-management')}>
                        จัดการความยินยอม
                      </Button>
                      <Button startIcon={<EditIcon />} onClick={() => setIsEditing(true)}>
                        {t('common.edit')}
                      </Button>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1}>
                      <Button
                        startIcon={<CancelIcon />}
                        onClick={() => {
                          setIsEditing(false);
                          setFormData({
                            first_name: user?.first_name || '',
                            last_name: user?.last_name || '',
                            email: user?.email || '',
                            avatar_url: user?.avatar_url || '',
                          });
                        }}
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveProfile}
                        disabled={loading}
                      >
                        {t('common.save')}
                      </Button>
                    </Stack>
                  )}
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Stack spacing={3}>
                  <TextField
                    label="First Name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    disabled={!isEditing || loading}
                    fullWidth
                  />

                  <TextField
                    label="Last Name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    disabled={!isEditing || loading}
                    fullWidth
                  />

                  <TextField
                    label="Email"
                    value={formData.email}
                    disabled
                    fullWidth
                    helperText="Email cannot be changed"
                  />

                  <TextField
                    label="Role"
                    value={user?.role}
                    disabled
                    fullWidth
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Stack>

      {/* Camera Dialog */}
      <Dialog
        open={cameraDialogOpen}
        onClose={handleCloseCamera}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('common.capturePhoto')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} alignItems="center">
            <Box
              sx={{
                width: '100%',
                bgcolor: 'black',
                borderRadius: 1,
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 360,
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  maxHeight: '480px',
                  display: isCameraReady ? 'block' : 'none',
                }}
              />
              {!isCameraReady && (
                <Typography color="white">Starting camera...</Typography>
              )}
            </Box>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCamera}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            startIcon={<CameraIcon />}
            onClick={handleCapturePhoto}
            disabled={!isCameraReady || loading}
          >
            {t('common.capturePhoto')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserProfile;
